/// <reference path="./dts/jquery.d.ts" />

import { Scene } from "./Scene"
import { NumberUtils, NumPair } from "./Misc/Utils"
import { UIUpdate } from "./UI/UIUpdate";
import { BObject } from "./Objects/BObject";
import { InputHandler } from './Input/InputHandler';
import { canvasSettings } from './Settings/CanvasSettings';
import { featureFlags } from "./Settings/FeatureFlags";

export class BezieApp {
    private inputHandler: InputHandler = new InputHandler(window.document)
    private canvasSettings = canvasSettings
    public featureFlags = featureFlags

    private scene : Scene

    private playStatus : number
    
    public static PLAY_STATUS_STOP = 0
    public static PLAY_STATUS_PLAY = 1
    public static PLAY_STATUS_PAUSE = 2

    //temp store. move to a diff store 
    private colorPallette = "a" //a=simple b=advanced
    getColorPallete(): string{
        return this.colorPallette;
    }
    setColorPallete(val: string){
        if(val == "b") 
            this.colorPallette = "b";
        else   
            this.colorPallette = "a";
    }

    //globals
    private lastRecTime : number = 0

    constructor(){
        this.canvasSettings.setDimens(800,600);
        this.canvasSettings.setAnimVals(24,1);
        this.scene = new Scene(this.inputHandler);
        this.playStatus = BezieApp.PLAY_STATUS_STOP;
    }

    init(){
        this.initCanvas();
        this.lastRecTime = Date.now();
    }

    initCanvas(){
        let canvas = this.getCanvas();
        canvas.width = this.canvasSettings.width;
        canvas.height = this.canvasSettings.height;
         
        let outlineCanvas = this.getOutlineCanvas();
        this.inputHandler.registerForEvents(outlineCanvas, canvas);
    }

    draw = () => {
        if(this.isTimeToRefresh()){
            this.drawScene();
        }
        requestAnimationFrame(this.draw);
    }

    drawScene(): void{
        let context  = this.getCanvas().getContext('2d');
        let outlineContext = this.getOutlineCanvas().getContext('2d');
        
        if(context){
            this.clearCanvas(context);
            this.scene.draw(context, this.playStatus);
            
            if(outlineContext){
                this.clearOutlineCanvas();
                this.preOutlineCanvasRender(outlineContext);
                this.scene.drawSelObjectOutline(outlineContext, this.playStatus);
                this.postOutlineCanvasRender(outlineContext);
            }else{
                this.scene.drawSelObjectOutline(context, this.playStatus);
            }
        }
    }

    isTimeToRefresh() : boolean{
        var curTime : number = Date.now();
        var delta : number = curTime - this.lastRecTime;
        if( delta > this.canvasSettings.refreshInterval){
            this.lastRecTime = curTime - (delta % this.canvasSettings.refreshInterval);
            return true;
        }
        return false;
    }

    clearCanvas(canvasCtx : CanvasRenderingContext2D) : void{
        canvasCtx.clearRect(0, 0, this.canvasSettings.width, this.canvasSettings.height);
        canvasCtx.fillStyle = 'white';
        canvasCtx.fillRect(0, 0, this.canvasSettings.width, this.canvasSettings.height);
    }

    clearOutlineCanvas(): void{
        let outlineCanvas = this.getOutlineCanvas();
        let outlineContext = outlineCanvas.getContext('2d');
        if(outlineContext){
            outlineContext.clearRect(0, 0, outlineCanvas.width, outlineCanvas.height);
        }
    }

    //Move origin of outline canvas to coincide with main canvas
    preOutlineCanvasRender(outlineCanvasCtx: CanvasRenderingContext2D):void{
        outlineCanvasCtx.save();
        let innerRect = this.getCanvas().getBoundingClientRect();
        let outerRect = this.getOutlineCanvas().getBoundingClientRect();
        outlineCanvasCtx.translate(innerRect.left-outerRect.left, innerRect.top-outerRect.top);
    }

    postOutlineCanvasRender(outlineCanvasCtx: CanvasRenderingContext2D): void{
        outlineCanvasCtx.restore();
    }

    getCanvas = () => {
        return <HTMLCanvasElement> document.getElementById('canvas');
    }

    getOutlineCanvas = () => {
        return <HTMLCanvasElement> document.getElementById('outlineCanvas');
    }

    playOrPause() :void{
        if(this.featureFlags.enableAnimations){
            if(this.playStatus == BezieApp.PLAY_STATUS_PLAY){
                this.playStatus = BezieApp.PLAY_STATUS_PAUSE
            }else {
                if(this.playStatus == BezieApp.PLAY_STATUS_STOP){
                    this.scene.prepareToPlay();
                }
                this.playStatus = BezieApp.PLAY_STATUS_PLAY
            }
        }else{
            this.playStatus = BezieApp.PLAY_STATUS_STOP
        }
        console.log("play status set to :"+this.playStatus+" anim:"+this.featureFlags.enableAnimations);
    }

    stop() :void{
        this.playStatus = BezieApp.PLAY_STATUS_STOP;
        this.scene.resetObjectPos();

    }

    getPlayPauseStatus() : number{
        return this.playStatus
    }

    setFps(tFps : number) : void{
        if(Number(tFps)!= NaN && tFps > 0){
            this.canvasSettings.fps = tFps;
            this.canvasSettings.refreshInterval = 1000/this.canvasSettings.fps;
        }
        console.log("fps rec:"+tFps+" set:"+this.canvasSettings.fps);
    }

    getFps(): number{
        return this.canvasSettings.fps;
    }

    setDimension(twidth: number, theight: number): void{
        if(NumberUtils.isNumber([twidth, theight]) && twidth > 0 && theight > 0){
            this.canvasSettings.setDimens(twidth, theight);
            this.init();
            console.log("dim set:"+this.canvasSettings.width+", "+this.canvasSettings.height);
        }
        console.log("dim rec:"+[twidth,theight]+" set:"+[this.canvasSettings.width,this.canvasSettings.height]);
    }

    getDimension(): NumPair {
        return [this.canvasSettings.width, this.canvasSettings.height];
    }

    getScene() : Scene{
        return this.scene;
    }

    getInputHandler(): InputHandler{
        return this.inputHandler;
    }

    saveAs(type: string): void{
        this.preRendering();
        UIUpdate.notifyonRenderStart();
        if(type=='jpeg' || type=='png'){
            var dataUrl = this.getCanvas().toDataURL('image/'+type, 1.0);
            UIUpdate.notifyonRenderFinish(dataUrl);
        }else{
            UIUpdate.notifyonRenderAbort();
        }
        this.postRendering();
    }

    //Dependent on gif.js. 
    //@param gif is a GIF object of gif.js
    saveAsGif(gif: any): void{
        this.preRendering();

        var canvas = this.getNewCanvas();
        var context = canvas.getContext('2d');
        var delayInMs = (1/this.canvasSettings.fps)*1000;
        
        if(context!=null){
            for(var i=0; i<this.scene.getTotalFrames(); i++){
                this.clearCanvas(context);
                this.scene.draw(context, BezieApp.PLAY_STATUS_PLAY);
                gif.addFrame(context, {delay: delayInMs, copy: true});
            }
        }else{
            console.log("Context is null!!!");
        }
        
        gif.on('start', function(){
            UIUpdate.notifyonRenderStart();
        });
        gif.on('progress', function(progressVal:number){
            UIUpdate.notifyonRenderProgress(progressVal);
        });
        gif.on('finished', function(blob:any) {
            UIUpdate.notifyonRenderFinish(blob);
        });
        gif.on('abort', function(){
            UIUpdate.notifyonRenderAbort();
        });
        
        
        console.log("gif.render ... delay: "+delayInMs+"ms");
        gif.render();

        this.postRendering();
    }

    /*saveToGif() : void{
        this.preRendering();
        console.log('saving to gif');
        
        var capturer = new CCapture( { 
            format: 'gif',
            framerate: this.fps,
            name: 'bezie-gif-'+Date.now(),
            workersPath: Paths.SCRIPT_LIBRARY
        });
        capturer.start();

        if(this.scene!=null){
            this.scene.resetObjectPos();
            
            var canvas = this.getNewCanvas();
            var context = canvas.getContext('2d');
            
            for(var i=0; i<this.scene.getTotalFrames(); i++){
                //console.log("capturing frame "+i);
                this.clearCanvas(context);
                this.scene.draw(context, BezieApp.PLAY_STATUS_PLAY);

                capturer.capture(canvas);
            }
            capturer.stop();
            console.log('saving captured frames');
            //capturer.save( function(blob:any){console.log("callback after rendering!"); UIUpdate.closeSaveToGifDialog()} );
            capturer.save();
        }
        
        this.postRendering();
    }*/

    preRendering(){
        console.log("pre rendering");
        this.stop();
        this.scene.isRendering = true;
    }

    postRendering(){
        console.log("post rendering");
        this.scene.isRendering = false;
    }

    getNewCanvas() : any{
        var newCanvas = document.createElement('canvas');
        newCanvas.height = this.canvasSettings.height;
        newCanvas.width = this.canvasSettings.width;
        return newCanvas;
    }

    addBObjectToScene(object: BObject): boolean{
        if(object){
            this.getScene().addBObject(object);
            return true;
        }
        return false;
    }
}